import { createSignal, onCleanup, onMount } from "solid-js";

import { sdkManager } from "@/lib/sdk/sdkManager";

/**
 * 检测页面滚动百分比的 Hook - 简化版本
 *
 * @param page 页面标识，用于上报
 * @param threshold 滚动阈值百分比（0-1），默认为 0.5（50%）
 * @param time 上报时间间隔，默认为 10 秒
 * @returns 返回滚动状态、滚动距离和滚动百分比
 */
export function usePageScrollDetection(
  page: string,
  threshold: number = 0.5,
  time: number = 10,
) {
  const [isScrolled, setIsScrolled] = createSignal(false);
  const [scrollDistance, setScrollDistance] = createSignal(0);
  const [scrollPercentage, setScrollPercentage] = createSignal(0);
  const [isReported, setIsReported] = createSignal(false);
  let timmer: NodeJS.Timeout;

  const handleScroll = () => {
    if (typeof window === "undefined") return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    const maxScroll = documentHeight - windowHeight;

    // 计算滚动百分比
    const percentage = maxScroll > 0 ? scrollTop / maxScroll : 0;
    setScrollDistance(scrollTop);
    setScrollPercentage(percentage);
    setIsScrolled(percentage > threshold);

    if (percentage > threshold) {
      if (timmer) {
        clearTimeout(timmer);
      }
      timmer = setTimeout(() => {
        if (isReported()) {
          return;
        }
        setIsReported(true);
        sdkManager.tdReportEvent(page, {
          page_scroll: percentage,
        });
      }, time);
    } else {
      if (timmer) {
        clearTimeout(timmer);
      }
    }
  };

  onMount(() => {
    if (typeof window === "undefined") return;

    // 初始检查
    handleScroll();

    // 添加滚动监听器到 window
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    console.log(`[${page}] 页面滚动监听器已添加`);
  });

  onCleanup(() => {
    if (typeof window === "undefined") return;
    console.log(`[${page}] 页面滚动监听器已移除`);
    if (timmer) {
      clearTimeout(timmer);
    }

    window.removeEventListener("scroll", handleScroll);
    window.removeEventListener("resize", handleScroll);
  });

  return {
    isScrolled,
    scrollDistance,
    scrollPercentage,
  };
}
